import { z } from "zod";
import { isoDate, moneyMinor, optionalEmail, optionalText, phoneInput, text, uuid } from "./common";

/* ======================================================================================== labs */

export const LAB_RESTORATION_TYPES = [
  "crown", "bridge", "veneer", "inlay_onlay", "implant_crown", "implant_abutment",
  "denture_complete", "denture_partial", "night_guard", "aligner", "retainer", "study_model", "other",
] as const;
export type LabRestorationType = (typeof LAB_RESTORATION_TYPES)[number];

export const LAB_RESTORATION_LABELS: Record<LabRestorationType, string> = {
  crown: "Crown", bridge: "Bridge", veneer: "Veneer", inlay_onlay: "Inlay / onlay", implant_crown: "Implant crown",
  implant_abutment: "Implant abutment", denture_complete: "Complete denture", denture_partial: "Partial denture",
  night_guard: "Night guard", aligner: "Aligner", retainer: "Retainer", study_model: "Study model", other: "Other",
};

export const LAB_CASE_STATUSES = ["created", "sent", "in_production", "ready", "received", "delivered", "cancelled"] as const;
export type LabCaseStatus = (typeof LAB_CASE_STATUSES)[number];
export const LAB_CASE_STATUS_LABELS: Record<LabCaseStatus, string> = {
  created: "Not sent yet", sent: "At the lab", in_production: "In production", ready: "Ready at lab",
  received: "Received", delivered: "Fitted / delivered", cancelled: "Cancelled",
};
/** Allowed next statuses (the API enforces the same map). */
export const LAB_CASE_NEXT: Record<LabCaseStatus, LabCaseStatus[]> = {
  created: ["sent", "cancelled"],
  sent: ["in_production", "ready", "received", "cancelled"],
  in_production: ["ready", "received", "cancelled"],
  ready: ["received", "cancelled"],
  received: ["delivered", "sent"], // "sent" = sent back for a remake / adjustment
  delivered: [],
  cancelled: [],
};

/** FDI tooth numbers: permanent 11–48, primary 51–85. */
export const fdiTooth = z.string().regex(/^([1-4][1-8]|[5-8][1-5])$/, "Use FDI tooth numbers like 16 or 46");

export const dentalLabInput = z.object({
  name: text(160),
  contactName: optionalText(120),
  phone: phoneInput,
  whatsappPhone: phoneInput,
  email: optionalEmail,
  address: optionalText(500),
  defaultTurnaroundDays: z.number().int().min(1).max(120).nullish().transform((v) => v ?? null),
  notes: optionalText(2000),
  isActive: z.boolean().default(true),
});
export type DentalLabInput = z.input<typeof dentalLabInput>;
export const updateDentalLabInput = dentalLabInput.extend({ version: z.number().int().positive() });

export interface DentalLab {
  id: string;
  name: string;
  contactName: string | null;
  phone: string | null;
  whatsappPhone: string | null;
  email: string | null;
  address: string | null;
  defaultTurnaroundDays: number | null;
  notes: string | null;
  isActive: boolean;
  openCases: number;
  version: number;
}

const labCaseFields = {
  doctorId: uuid,
  labId: uuid,
  treatmentPlanItemId: uuid.nullish().transform((v) => v ?? null),
  restorationType: z.enum(LAB_RESTORATION_TYPES),
  material: optionalText(120),
  toothCodes: z.array(fdiTooth).max(32).default([]),
  shade: optionalText(40),
  instructions: optionalText(4000),
  costMinor: moneyMinor.nullish().transform((v) => v ?? null),
  labInvoiceReference: optionalText(80),
};

export const labCaseInput = z.object({ patientId: uuid, branchId: uuid, ...labCaseFields });
export type LabCaseInput = z.input<typeof labCaseInput>;
export const updateLabCaseInput = z.object({ version: z.number().int().positive(), ...labCaseFields, expectedOn: isoDate.nullish().transform((v) => v ?? null) });

export const labCaseStatusInput = z
  .object({
    version: z.number().int().positive(),
    status: z.enum(LAB_CASE_STATUSES).exclude(["created"]),
    /** Day it happened (clinic calendar); defaults to today. */
    on: isoDate.nullish().transform((v) => v ?? null),
    /** When sending: when the lab promised it back (defaults to the lab's turnaround). */
    expectedOn: isoDate.nullish().transform((v) => v ?? null),
    note: optionalText(1000),
    cancelReason: optionalText(500),
  })
  .refine((v) => v.status !== "cancelled" || !!v.cancelReason, { path: ["cancelReason"], message: "Say why the case is cancelled" });

export const labCaseListQuery = z.object({
  status: z.enum(["open", "overdue", "received", "closed", "all"]).default("open"),
  patientId: uuid.optional(),
  labId: uuid.optional(),
  doctorId: uuid.optional(),
  branchId: uuid.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(100),
});

export interface LabCaseEvent {
  id: string;
  fromStatus: LabCaseStatus | null;
  toStatus: LabCaseStatus;
  occurredAt: string;
  note: string | null;
  actor: string | null;
}

export interface LabCase {
  id: string;
  number: string;
  patient: { id: string; fullName: string; patientCode: string; phone: string | null };
  doctor: { id: string; fullName: string };
  branch: { id: string; name: string };
  lab: { id: string; name: string; phone: string | null; whatsappPhone: string | null };
  treatmentPlanItemId: string | null;
  restorationType: LabRestorationType;
  material: string | null;
  toothCodes: string[];
  shade: string | null;
  instructions: string | null;
  status: LabCaseStatus;
  sentOn: string | null;
  expectedOn: string | null;
  receivedOn: string | null;
  deliveredOn: string | null;
  /** Expected date passed and the work is not back. */
  overdue: boolean;
  costMinor: number | null;
  currency: string | null;
  labInvoiceReference: string | null;
  cancelReason: string | null;
  createdAt: string;
  version: number;
  events?: LabCaseEvent[];
}

/* =================================================================================== inventory */

export const INVENTORY_KINDS = ["material", "consumable", "medicine", "equipment"] as const;
export type InventoryKind = (typeof INVENTORY_KINDS)[number];

/** Quantities have at most 3 decimals (e.g. 0.5 ml). */
const qty = z
  .number()
  .positive()
  .max(1_000_000_000)
  .refine((n) => Math.abs(Math.round(n * 1000) - n * 1000) < 1e-6, "Use at most 3 decimals");

export const supplierInput = z.object({
  name: text(160),
  contactName: optionalText(120),
  phone: phoneInput,
  email: optionalEmail,
  address: optionalText(500),
  taxRegistrationId: optionalText(30),
  paymentTermsDays: z.number().int().min(0).max(365).nullish().transform((v) => v ?? null),
  notes: optionalText(2000),
  isActive: z.boolean().default(true),
});
export const updateSupplierInput = supplierInput.extend({ version: z.number().int().positive() });

export interface Supplier {
  id: string;
  name: string;
  contactName: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
  taxRegistrationId: string | null;
  paymentTermsDays: number | null;
  notes: string | null;
  isActive: boolean;
  version: number;
}

export const inventoryItemInput = z.object({
  sku: z.string().trim().regex(/^[A-Za-z0-9][A-Za-z0-9._/-]*$/, "Letters, digits and . _ / - only").max(64),
  name: text(200),
  category: optionalText(80),
  kind: z.enum(INVENTORY_KINDS),
  unit: z.string().trim().toLowerCase().regex(/^[a-z][a-z_]*$/, "One word, like piece, box, ml").max(20),
  reorderLevel: z.number().min(0).max(1_000_000_000).nullish().transform((v) => v ?? null),
  reorderQuantity: qty.nullish().transform((v) => v ?? null),
  trackBatches: z.boolean().default(false),
  defaultSupplierId: uuid.nullish().transform((v) => v ?? null),
  notes: optionalText(2000),
  isActive: z.boolean().default(true),
  /** Pharmacy counter (A5): formulary medicine this stock is, selling price per unit (tax included), tax and HSN. */
  medicineId: uuid.nullish().transform((v) => v ?? null),
  salePriceMinor: z.number().int().min(0).max(10_000_000_00).nullish().transform((v) => v ?? null),
  taxCategoryId: uuid.nullish().transform((v) => v ?? null),
  hsnCode: z.string().trim().toUpperCase().regex(/^[A-Z0-9.]{2,12}$/, "e.g. 3004").nullish().or(z.literal("")).transform((v) => v || null),
});
export type InventoryItemInput = z.input<typeof inventoryItemInput>;
export const updateInventoryItemInput = inventoryItemInput.extend({ version: z.number().int().positive() });

export interface InventoryBatchLevel {
  batchId: string;
  batchNumber: string;
  expiryDate: string | null;
  onHand: number;
}

export interface InventoryItem {
  id: string;
  sku: string;
  name: string;
  category: string | null;
  kind: InventoryKind;
  unit: string;
  reorderLevel: number | null;
  reorderQuantity: number | null;
  trackBatches: boolean;
  defaultSupplier: { id: string; name: string } | null;
  notes: string | null;
  isActive: boolean;
  medicine: { id: string; name: string } | null;
  salePriceMinor: number | null;
  taxCategoryId: string | null;
  hsnCode: string | null;
  /** On hand in the requested branch (all branches when none is given). */
  onHand: number;
  lowStock: boolean;
  /** Earliest expiry among batches with stock. */
  nextExpiry: string | null;
  version: number;
  batches?: InventoryBatchLevel[];
}

export const inventoryListQuery = z.object({
  branchId: uuid.optional(),
  q: z.string().trim().max(100).optional(),
  filter: z.enum(["all", "low", "expiring", "inactive"]).default("all"),
  /** "expiring" = a batch with stock expires within this many days. */
  days: z.coerce.number().int().min(1).max(730).default(60),
});

export const STOCK_MOVEMENT_TYPES = ["opening", "purchase", "usage", "adjustment", "transfer_in", "transfer_out", "expired", "return"] as const;
export type StockMovementType = (typeof STOCK_MOVEMENT_TYPES)[number];
export const STOCK_MOVEMENT_LABELS: Record<StockMovementType, string> = {
  opening: "Opening stock", purchase: "Received", usage: "Used", adjustment: "Count correction", transfer_in: "Transfer in",
  transfer_out: "Transfer out", expired: "Expired / discarded", return: "Returned to supplier",
};

/** Manual stock entry. Quantity is always positive; the type decides the direction (adjustment uses `direction`). */
export const stockMovementInput = z
  .object({
    branchId: uuid,
    itemId: uuid,
    type: z.enum(["opening", "usage", "adjustment", "expired", "return"]),
    direction: z.enum(["in", "out"]).default("in"),
    quantity: qty,
    /** Batch-tracked items: an existing batch, or a new batch number (+ expiry) for stock coming in. */
    batchId: uuid.nullish().transform((v) => v ?? null),
    batchNumber: optionalText(64),
    expiryDate: isoDate.nullish().transform((v) => v ?? null),
    unitCostMinor: moneyMinor.nullish().transform((v) => v ?? null),
    reason: optionalText(500),
    labCaseId: uuid.nullish().transform((v) => v ?? null),
  })
  .refine((v) => !["adjustment", "expired", "return"].includes(v.type) || !!v.reason, { path: ["reason"], message: "Give a reason" });
export type StockMovementInput = z.input<typeof stockMovementInput>;

export const stockTransferInput = z
  .object({
    itemId: uuid,
    fromBranchId: uuid,
    toBranchId: uuid,
    quantity: qty,
    batchId: uuid.nullish().transform((v) => v ?? null),
    reason: optionalText(500),
  })
  .refine((v) => v.fromBranchId !== v.toBranchId, { path: ["toBranchId"], message: "Choose a different branch" });

export interface StockMovement {
  id: string;
  type: StockMovementType;
  quantity: number;
  branch: { id: string; name: string };
  batchNumber: string | null;
  unitCostMinor: number | null;
  reason: string | null;
  referenceType: string | null;
  referenceId: string | null;
  occurredAt: string;
  recordedBy: string | null;
}

/* ============================================================================= purchase orders */

export const PURCHASE_ORDER_STATUSES = ["draft", "ordered", "partially_received", "received", "cancelled"] as const;
export type PurchaseOrderStatus = (typeof PURCHASE_ORDER_STATUSES)[number];

export const purchaseOrderInput = z.object({
  supplierId: uuid,
  branchId: uuid,
  expectedOn: isoDate.nullish().transform((v) => v ?? null),
  notes: optionalText(2000),
  lines: z
    .array(z.object({ itemId: uuid, quantity: qty, unitCostMinor: moneyMinor }))
    .min(1, "Add at least one item")
    .max(100),
});
export type PurchaseOrderInput = z.input<typeof purchaseOrderInput>;
export const updatePurchaseOrderInput = purchaseOrderInput.extend({ version: z.number().int().positive() });

export const receivePurchaseOrderInput = z.object({
  version: z.number().int().positive(),
  supplierInvoiceReference: optionalText(80),
  lines: z
    .array(
      z.object({
        lineId: uuid,
        quantity: qty,
        batchNumber: optionalText(64),
        expiryDate: isoDate.nullish().transform((v) => v ?? null),
      }),
    )
    .min(1)
    .max(100),
});

export interface PurchaseOrderLine {
  id: string;
  lineNo: number;
  item: { id: string; sku: string; name: string; unit: string; trackBatches: boolean };
  quantityOrdered: number;
  quantityReceived: number;
  unitCostMinor: number;
  totalMinor: number;
}

export interface PurchaseOrder {
  id: string;
  number: string;
  supplier: { id: string; name: string; phone: string | null; email: string | null };
  branch: { id: string; name: string };
  status: PurchaseOrderStatus;
  orderDate: string | null;
  expectedOn: string | null;
  currency: string;
  totalMinor: number;
  supplierInvoiceReference: string | null;
  notes: string | null;
  createdAt: string;
  version: number;
  lines: PurchaseOrderLine[];
}

export const purchaseOrderListQuery = z.object({
  status: z.enum(["open", "draft", "received", "cancelled", "all"]).default("open"),
  supplierId: uuid.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(100),
});
